import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Sprout, Scale } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const PILLARS = [
  { icon: Sprout, title: "Vivência de campo", text: "Conhecemos a rotina da propriedade e falamos a língua do produtor." },
  { icon: ShieldCheck, title: "Rigor técnico", text: "Projetos, laudos e perícias fundamentados e bem documentados." },
  { icon: Scale, title: "Visão integrada", text: "Agronomia, engenharia, crédito e jurídico tratados de forma conjunta." },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["-4%", "14%"]);

  return (
    <section id="sobre" ref={ref} data-testid="about-section" className="relative mx-auto max-w-[1400px] px-5 py-8 sm:px-8 sm:py-14 md:py-36">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-start lg:gap-10">
        <div className="lg:col-span-6">
          <Reveal><Overline>Sobre a Melo Agro</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-[#1A2218] sm:mt-8 sm:text-5xl lg:text-6xl">
              A ciência do solo encontra a <em className="text-[#5D995E]">exatidão</em> da engenharia.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#2C332A]/85 md:text-lg">
              A Melo Agro Engenharia nasceu para dar segurança técnica a quem vive do campo e da cidade. Reunimos agronomia e engenharia
              para cuidar do que pesa nas decisões do produtor: o crédito, o valor da terra, a regularidade documental e a proteção da safra.
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#2C332A]/85 md:text-lg">
              Atendemos produtores, empresas, bancos e escritórios de advocacia em toda a metade sul do Rio Grande do Sul, com atendimento próximo
              e documentos tecnicamente fundamentados e rastreáveis.
            </p>
          </Reveal>
          <div className="mt-6 flex snap-x snap-mandatory gap-3 overflow-x-auto rounded-2xl sm:grid sm:grid-cols-3 sm:gap-px sm:overflow-hidden sm:rounded-2xl">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 * i} className="min-w-[72%] snap-center sm:min-w-0 sm:bg-[#F9F8F3]">
                <div data-testid={`about-pillar-${i}`} className="h-full rounded-2xl border border-[#E5E0D5] bg-white p-5 transition-colors duration-500 hover:bg-[#233D25] sm:rounded-none sm:border-0 sm:bg-[#F9F8F3]">
                  <p.icon className="h-6 w-6 text-[#5D995E] transition-transform duration-500 group-hover:-rotate-12" />
                  <h3 className="mt-5 font-serif text-xl text-[#1A2218] transition-colors duration-500 group-hover:text-[#F9F8F3]">{p.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-[#4A5549] transition-colors duration-500 group-hover:text-[#F9F8F3]/75">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative hidden sm:block lg:col-span-6">
          <div className="grid grid-cols-12 gap-4">
            <motion.div style={{ y: y1 }} className="col-span-8 overflow-hidden rounded-[28px] rounded-tr-[120px]">
              <div className="relative">
                <img src="/img/engenheiro-lucas-soja.webp" alt="Lucas Melo, engenheiro civil e agrônomo da Melo Agro Engenharia, em lavoura de soja na Metade Sul do RS" width={960} height={1280} loading="lazy" className="aspect-[4/3] h-full w-full object-cover transition-transform duration-[1.4s] hover:scale-105 sm:aspect-[3/4]" />
              </div>
            </motion.div>
            <motion.div style={{ y: y2 }} className="col-span-4 mt-24 hidden space-y-4 sm:block">
              <div className="overflow-hidden rounded-full">
                <img src="/img/pastagem-gado.webp" alt="Gado de corte em pastagem na Metade Sul do RS — foto real da Melo Agro Engenharia" width={961} height={1280} loading="lazy" className="aspect-[1/2] w-full object-cover" />
              </div>
            </motion.div>
          </div>
          <div className="absolute -bottom-6 left-4 rounded-2xl bg-[#233D25] px-6 py-5 text-[#F9F8F3] shadow-xl sm:left-8">
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#8FC690]">Serviços integrados</p>
            <p className="mt-1 font-serif text-4xl">8 frentes</p>
          </div>
        </div>
      </div>
    </section>
  );
};
